import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Facebook,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  Package,
  DollarSign,
  Sparkles,
  Eye,
  ShoppingBag,
} from "lucide-react";
import { API } from "@/App";

export default function Marketplace({ user }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewListing, setPreviewListing] = useState(null);
  const [copied, setCopied] = useState(false);

  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);

  const [form, setForm] = useState({
    product_id: "",
    seller_id: "",
    price: "",
  });

  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      
      const [listingsRes, productsRes, sellersRes] = await Promise.all([
        fetch(`${API}/marketplace/listings${params.toString() ? `?${params.toString()}` : ""}`, { credentials: "include" }),
        fetch(`${API}/products`, { credentials: "include" }),
        fetch(`${API}/sellers`, { credentials: "include" }),
      ]);

      if (listingsRes.ok) setListings(await listingsRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (sellersRes.ok) setSellers(await sellersRes.json());
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.product_id) {
      toast.error("Please select a product");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/marketplace/listings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          price: form.price ? parseFloat(form.price) : null,
          seller_id: form.seller_id || null,
        }),
        credentials: "include",
      });

      if (response.ok) {
        const newListing = await response.json();
        toast.success("Listing created with AI-generated content!");
        setDialogOpen(false);
        setForm({ product_id: "", seller_id: "", price: "" });
        fetchData();
        // Show preview
        setPreviewListing(newListing);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create listing");
      }
    } catch (error) {
      toast.error("Error creating listing");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusUpdate = async (listingId, newStatus) => {
    try {
      const response = await fetch(`${API}/marketplace/listings/${listingId}/status?status=${newStatus}`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(`Listing marked as ${newStatus}`);
        fetchData();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const handleMarkSold = async (listing) => {
    try {
      const response = await fetch(`${API}/marketplace/listings/${listing.listing_id}/mark-sold`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Item sold! Inventory updated and commission recorded.");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to mark as sold");
      }
    } catch (error) {
      toast.error("Error processing sale");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/10 text-gray-500 border-gray-500/20",
      active: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      sold: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      expired: "bg-red-500/10 text-red-500 border-red-500/20",
    };
    return (
      <Badge className={`rounded-sm border ${styles[status] || styles.draft}`}>
        {status}
      </Badge>
    );
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="marketplace-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Facebook className="h-8 w-8 text-blue-600" />
              Marketplace
            </h1>
            <p className="text-muted-foreground">
              Generate listings for Facebook Marketplace
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="create-listing-btn">
                <Sparkles className="h-4 w-4 mr-2" />
                Create Listing
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  AI-Powered Listing
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Select a product and we'll generate an optimized Facebook Marketplace listing using AI.
                </p>
                
                <div className="space-y-2">
                  <Label>Product *</Label>
                  <Select
                    value={form.product_id}
                    onValueChange={(v) => {
                      const product = products.find(p => p.product_id === v);
                      setForm({
                        ...form,
                        product_id: v,
                        price: product?.retail_price?.toString() || "",
                      });
                    }}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Listing Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      className="rounded-sm font-data pl-8"
                      placeholder="Leave empty for retail price"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Assign Seller (Optional)</Label>
                  <Select
                    value={form.seller_id || "none"}
                    onValueChange={(v) => setForm({ ...form, seller_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="No seller assigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No seller</SelectItem>
                      {sellers.filter(s => s.status === "active").map((s) => (
                        <SelectItem key={s.seller_id} value={s.seller_id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} className="rounded-sm flex-1">
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Listing
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter */}
        <Card className="rounded-sm border">
          <CardContent className="p-4">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48 rounded-sm">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Listings Table */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 skeleton-pulse" />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="p-8 text-center">
                <Facebook className="h-12 w-12 mx-auto mb-4 text-blue-600 opacity-50" />
                <p className="text-muted-foreground mb-4">No marketplace listings yet</p>
                <Button onClick={() => setDialogOpen(true)} className="rounded-sm">
                  Create your first listing
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Seller</TableHead>
                    <TableHead className="font-data text-right">Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listings.map((listing) => (
                    <TableRow key={listing.listing_id} className="data-row">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          {listing.product?.name || "Unknown"}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {listing.title}
                      </TableCell>
                      <TableCell>
                        {listing.seller?.name || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="font-data text-right">
                        {formatCurrency(listing.price)}
                      </TableCell>
                      <TableCell>{getStatusBadge(listing.status)}</TableCell>
                      <TableCell className="font-data text-sm text-muted-foreground">
                        {new Date(listing.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setPreviewListing(listing)}
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {listing.status === "draft" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStatusUpdate(listing.listing_id, "active")}
                              title="Mark Active"
                              className="text-blue-500"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          {listing.status === "active" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleMarkSold(listing)}
                              title="Mark Sold"
                              className="text-emerald-500"
                            >
                              <ShoppingBag className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={!!previewListing} onOpenChange={() => setPreviewListing(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Facebook className="h-5 w-5 text-blue-600" />
                Listing Preview
              </DialogTitle>
            </DialogHeader>
            {previewListing && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-lg">{previewListing.title}</h3>
                    <p className="font-data text-xl font-bold text-primary">
                      {formatCurrency(previewListing.price)}
                    </p>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                    {previewListing.description}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(previewListing.title)}
                    className="rounded-sm flex-1"
                  >
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    Copy Title
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(previewListing.description)}
                    className="rounded-sm flex-1"
                  >
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    Copy Description
                  </Button>
                </div>

                <Button
                  onClick={() => copyToClipboard(`${previewListing.title}\n\n${previewListing.description}\n\nPrice: ${formatCurrency(previewListing.price)}`)}
                  className="rounded-sm w-full"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Full Listing
                </Button>

                {previewListing.status === "draft" && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleStatusUpdate(previewListing.listing_id, "active");
                        setPreviewListing(null);
                      }}
                      className="rounded-sm flex-1"
                    >
                      Mark as Active
                    </Button>
                  </div>
                )}

                {previewListing.status === "active" && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      onClick={() => {
                        handleMarkSold(previewListing);
                        setPreviewListing(null);
                      }}
                      className="rounded-sm flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Mark as Sold
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
